import {
  collectionScope,
  Currency,
  getMerchantStructureByMerchantId,
  LoanStatusExternal,
} from '@nemuru-eng/js-api-client';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ChannelAndAgentMetrics } from '@/components/modules/Metrics/ChannelAgentMetricViews/components/ChannelAndAgentMetrics';
import { MetricsControllers } from '@/components/modules/Metrics/ChannelAgentMetricViews/components/MetricsControllers';
import { loanStatusExternalValues } from '@/components/modules/Metrics/ChannelAgentMetricViews/constants/LoanStatusExternalValues';
import { MerchantChannelAgentMetricViews } from '@/components/modules/Metrics/shared/components/MerchantChannelAgentMetricViews';
import { useLiveMetrics } from '@/components/modules/Metrics/shared/hooks/useLiveMetrics';
import { useMetricMerchantChannelAgentViews } from '@/components/modules/Metrics/shared/hooks/useMetricMerchantChannelAgentViews';
import { useMetricsScope } from '@/components/modules/Metrics/shared/hooks/useMetricsScope';
import { useTimeInterval } from '@/components/modules/Metrics/shared/hooks/useTimeInterval';
import { ScoreCardMetricKey } from '@/components/modules/Metrics/shared/types/ScoreCardMetricKey';
import { useCache } from '@/hooks/requests/useCache';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

const currency = Currency.EUR;

const MetricChannelViews = () => {
  const { merchantId, channelId } = useParams();

  const routeScope = useMemo(() => {
    if (!channelId) return undefined;
    try {
      return collectionScope.channel(channelId);
    } catch {
      return undefined;
    }
  }, [channelId]);
  useLiveMetrics(useMetricsScope(routeScope));

  if (!merchantId) return null;

  const { timeInterval, setTimeInterval } = useTimeInterval();
  const { data: merchantStructure } = useCache(getMerchantStructureByMerchantId, { merchantId });

  const [selectedLoanStatusExternal, setSelectedLoanStatusExternal] = useState<LoanStatusExternal>(
    LoanStatusExternal.CONFIRMED,
  );
  const [selectedMetricsType, setSelectedMetricsType] = useState<ScoreCardMetricKey>(
    ScoreCardMetricKey.totalPrincipalAmount,
  );

  const { filteredMetrics, hasRequested } = useMetricMerchantChannelAgentViews({
    currency,
    timeInterval,
    loanStatusExternal: loanStatusExternalValues,
    merchantIds: [merchantId],
    channelId,
  });

  if (!timeInterval || !merchantStructure) return <></>;
  return (
    <>
      <MetricsControllers
        selectedLoanStatusExternal={selectedLoanStatusExternal}
        setSelectedLoanStatusExternal={setSelectedLoanStatusExternal}
        selectedMetricsType={selectedMetricsType}
        setSelectedMetricsType={setSelectedMetricsType}
        timeInterval={timeInterval}
        setTimeInterval={setTimeInterval}
      />
      <MerchantChannelAgentMetricViews
        currency={currency}
        selectedMetricsType={selectedMetricsType}
        timeInterval={timeInterval}
        loanStatusExternal={loanStatusExternalValues}
        filteredMetrics={filteredMetrics}
        hasRequested={hasRequested}
        setSelectedLoanStatusExternal={setSelectedLoanStatusExternal}
      />
      <ChannelAndAgentMetrics
        merchantStructure={merchantStructure}
        timeInterval={timeInterval}
        setTimeInterval={setTimeInterval}
        filteredMetrics={filteredMetrics}
        hasRequested={hasRequested}
        currency={currency}
        selectedLoanStatusExternal={selectedLoanStatusExternal}
        selectedMetricsType={selectedMetricsType}
        hideChannelMetrics
      />
    </>
  );
};

export const MetricChannelViewsModule = withPermissionsModule(MetricChannelViews);
